import {
  Hotel,
  Utensils,
  Ticket,
  Bus,
  Star,
  StickyNote,
  MapPin,
  Ship,
  Anchor,
  type LucideIcon,
} from "lucide-react";
import type { ItineraryItemType, SegmentVerdictStatus } from "@/lib/types";

/** Lucide icon for each itinerary item type. */
export const ITEM_ICON: Record<ItineraryItemType, LucideIcon> = {
  event: Star,
  lodging: Hotel,
  dining: Utensils,
  activity: Ticket,
  transfer: Bus,
  note: StickyNote,
  cruise: Ship,
  port: Anchor,
};

/** Friendly singular label for each item type. */
export const ITEM_LABEL: Record<ItineraryItemType, string> = {
  event: "Event",
  lodging: "Lodging",
  dining: "Dining",
  activity: "Activity",
  transfer: "Transfer",
  note: "Note",
  cruise: "Cruise",
  port: "Port of call",
};

/**
 * The types a TRAVELLER can pick in the add/edit dialog. "port" is absent on
 * purpose: a port day is written from the cruise line's published itinerary,
 * and hand-typing one would create exactly the unverifiable port this feature
 * exists to prevent.
 */
export const ITEM_TYPES: ItineraryItemType[] = [
  "event",
  "cruise",
  "lodging",
  "dining",
  "activity",
  "transfer",
  "note",
];

export { MapPin };

/** Format "HH:MM" (24h) -> "8:21 AM". Returns null on empty/garbage. */
export function formatTime(t: string | null | undefined): string | null {
  if (!t) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(t.trim());
  if (!m) return t;
  let h = Number(m[1]);
  const min = m[2];
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return h + ":" + min + " " + ampm;
}

/** Minutes -> "2h 15m" / "45m" / "3h". */
export function formatDuration(mins: number | null | undefined): string | null {
  if (mins == null || !Number.isFinite(mins) || mins <= 0) return null;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  if (h === 0) return m + "m";
  if (m === 0) return h + "h";
  return h + "h " + m + "m";
}

/** TZ shift minutes -> signed pill label like "+2h" / "-1h 30m". 0/null -> null. */
export function formatTzShift(mins: number | null | undefined): string | null {
  if (mins == null || mins === 0) return null;
  const sign = mins > 0 ? "+" : "−"; // proper minus sign
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return sign + m + "m";
  if (m === 0) return sign + h + "h";
  return sign + h + "h " + m + "m";
}

/** USD -> "$129" (no cents). */
export function formatPrice(p: number | null | undefined): string | null {
  if (p == null || !Number.isFinite(p)) return null;
  return "$" + Math.round(p).toLocaleString();
}

/** True when arrival date is strictly after departure date (red-eye / +1). */
export function arrivesNextDay(
  departDate: string | null | undefined,
  arriveDate: string | null | undefined,
): boolean {
  if (!departDate || !arriveDate) return false;
  return arriveDate > departDate;
}

/** Stops count -> "Nonstop" / "1 stop" / "N stops". Returns "" for null (unknown). */
export function stopsLabel(stops: number | null): string {
  if (stops == null) return "";
  if (stops === 0) return "Nonstop";
  return stops === 1 ? "1 stop" : stops + " stops";
}

export const VERDICT_META: Record<
  SegmentVerdictStatus,
  { label: string; tone: string; dot: string }
> = {
  clears: {
    label: "Clears",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
  },
  tight: {
    label: "Tight",
    tone: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-500",
  },
  too_late: {
    label: "Too late",
    tone: "border-red-200 bg-red-50 text-red-700",
    dot: "bg-red-500",
  },
};
